import type { EconCalcFact, EconSource } from '@shared/api';
import {
  dimensionField,
  fmtNumber,
  fmtRub,
  isMoneyMetric,
  labelPayloadKey,
  marginNote,
  metricUnit,
  revenueTotalNote,
} from '@entities/economic/types';

/**
 * Разложить payload (kind-зависимый `unknown`) в список «Поле: значение» с
 * русскими подписями ключей. Объект → пары; примитив/массив → строка целиком
 * (редкий случай — payload по контракту JSON-объект). Значения форматируем
 * по-русски: булевы → да/нет, числа → ru-RU, null/пусто → прочерк.
 */
export function payloadEntries(payload: unknown): { key: string; label: string; value: string }[] {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return [];
  }
  const obj = payload as Record<string, unknown>;
  return Object.keys(obj).map((key) => ({
    key,
    label: labelPayloadKey(key),
    value: formatPayloadValue(obj[key]),
  }));
}

/** Привести значение payload к человекочитаемой строке (русские да/нет, ru-RU
 *  числа, прочерк для пустого). Вложенные объекты — JSON-строкой (редко). */
export function formatPayloadValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toLocaleString('ru-RU') : '—';
  }
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Превратить произвольный payload в редактируемый JSON-текст (с отступами). */
export function payloadToText(payload: unknown): string {
  if (payload === null || payload === undefined) return '{}';
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return '{}';
  }
}

/** Форматировать значение факта по типу метрики: деньги → `… ₽`, объёмы/часы →
 *  число со своей единицей. */
export function fmtMetricValue(metric: string, value: number | null): string {
  if (value === null) return '—';
  if (isMoneyMetric(metric)) return fmtRub(value);
  const unit = metricUnit(metric);
  const num = fmtNumber(value);
  return unit ? `${num} ${unit}` : num;
}

/** Один ОБЪЕКТ с набором показателей. Объект — первичная единица БДР (ТЗ
 *  6.1.4): доход по договорам, затраты по статьям, маржа — в разрезе объекта. */
export interface FactGroup {
  /** Заголовок: наименование объекта (или «Без привязки к объекту»). */
  title: string;
  /** Есть ли у группы реальный объект — чтобы не дублировать его в разрезе. */
  hasObject: boolean;
  facts: EconCalcFact[];
}

/** Сгруппировать факты по `dimension.object`. Объект первичен (ТЗ «БДР по
 *  объектам»): доход по КС несёт № договора, затраты из 1С — без него, а общая
 *  ось обоих источников — объект. № договора показывается вторичным разрезом
 *  внутри (см. `factDimensionSegments`). Внутри группы сохраняем порядок фактов
 *  из API. Факты без объекта (как правило, прочие модули) — общей группой в конце. */
export function groupFactsByObject(facts: EconCalcFact[]): FactGroup[] {
  const order: (string | null)[] = [];
  const byObject = new Map<string | null, EconCalcFact[]>();

  for (const f of facts) {
    const object = dimensionField(f.dimension, 'object');
    if (!byObject.has(object)) {
      byObject.set(object, []);
      order.push(object);
    }
    byObject.get(object)?.push(f);
  }

  // Группа без объекта — в конец.
  order.sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return 0;
  });

  return order.map((object) => ({
    title: object ?? 'Без привязки к объекту',
    hasObject: object !== null,
    facts: byObject.get(object) ?? [],
  }));
}

/** Подгруппа фактов одной метрики внутри договора (после подгруппировки и
 *  сортировки по сумме). */
export interface MetricBucket {
  metric: string;
  facts: EconCalcFact[];
}

/** Свернуть факты группы договора в подгруппы по метрике: внутри подгруппы —
 *  сортировка по сумме (`value_fact`) по убыванию, чтобы крупнейшие статьи/
 *  объекты были сверху; `null`-суммы — в конец. Порядок самих подгрупп —
 *  по первому появлению метрики в API (сохраняет осмысленный порядок: доход →
 *  затраты → маржа). Так десятки фактов «Затраты по статье»/«Маржа» перестают
 *  выглядеть дублями — каждый различается разрезом и упорядочен по величине. */
export function bucketByMetric(facts: EconCalcFact[]): MetricBucket[] {
  const order: string[] = [];
  const byMetric = new Map<string, EconCalcFact[]>();
  for (const f of facts) {
    if (!byMetric.has(f.metric)) {
      byMetric.set(f.metric, []);
      order.push(f.metric);
    }
    byMetric.get(f.metric)?.push(f);
  }
  return order.map((metric) => {
    const bucket = (byMetric.get(metric) ?? []).slice();
    bucket.sort((a, b) => {
      const av = a.value_fact;
      const bv = b.value_fact;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return bv - av;
    });
    return { metric, facts: bucket };
  });
}

/** Сколько фактов одной метрики показываем без свёртки. Сверх — за кнопкой
 *  «ещё N», чтобы длинный разрез (статьи затрат, лицензии) не растягивал
 *  карточку договора на сотни строк. */
export const FACTS_VISIBLE_LIMIT = 12;

/** Итоговые денежные показатели дохода БДР, которые при неполной обработке
 *  нужно явно пометить «заведомо неполный» (часть источников не вошла). Доход
 *  складывается из КС-2/КС-3 по всем договорам/объектам — если упал источник,
 *  именно эти суммы оказываются огрызком. Затраты/маржа выводятся из них, но
 *  метим только сам доход, чтобы не зашумлять каждую строку. */
export const INCOMPLETE_REVENUE_METRICS: ReadonlySet<string> = new Set([
  'revenue_ks3',
  'revenue_ks3_cumulative',
  'revenue_ks2',
  'revenue_total',
]);

/** Примечание-неполнота для метрики bucket'а (вторичный текст под значением).
 *  Привязано к производным метрикам, чьё совпадение сумм без пояснения читается
 *  как ошибка: «Маржа» (нет затрат → = доходу) и «Доход всего» (нет КС-3/КС-2 →
 *  = одной составляющей). Условия — по фактам группы договора и источникам
 *  обработки (см. эвристики в entities). Прочие метрики — без примечания. */
export function metricNoteFor(
  metric: string,
  groupFacts: readonly EconCalcFact[],
  sources: readonly EconSource[],
): string | null {
  if (metric === 'margin') return marginNote(groupFacts);
  if (metric === 'revenue_total') return revenueTotalNote(groupFacts, sources);
  return null;
}
